import { useEffect, useState } from 'react';
import { ApiError } from '../../../services/api';
import { getInvoiceReminders } from '../../../services/admin-billing';
import type { BillingReminder } from '../../../types/adminBilling';
import {
  REMINDER_KIND_LABEL,
  REMINDER_OUTCOME_LABEL,
  REMINDER_SOURCE_LABEL,
  dateOnlyCell,
  instantCell,
} from './billingFormat';

interface InvoiceReminderHistoryProps {
  invoiceId: string;
}

/** The one line under the outcome that says why it went the way it did. */
const reasonFor = (reminder: BillingReminder): string | null => {
  if (reminder.failureReason) return `Failed: ${reminder.failureReason}`;
  if (reminder.suppressedReason) return `Not sent: ${reminder.suppressedReason}`;
  return null;
};

/**
 * Every reminder for one invoice: what went out, to whom, when, and why anything did not.
 *
 * **A suppressed or failed reminder is as important as a sent one**, and gets the same row rather
 * than being filtered out. "Nothing was sent because the organization has no billing contact" is
 * the answer to "why has this customer not paid", and an empty history would hide it behind a
 * guess.
 *
 * `anchor` is the date the reminder is *for* and is shown as such, beside the instant it actually
 * went out. They differ whenever the sender was behind, and conflating them makes a late run look
 * like an early one.
 *
 * **One history, both senders.** An organization with no Stripe customer is chased by this system;
 * one with a Stripe customer is chased by Stripe, and those rows are recorded here from its
 * webhooks after the fact. Every row says which, because "Sent" on a row Stripe sent would read as
 * a claim that we emailed the customer. A Stripe row carries no `templateKey`, since its dunning
 * does not use our copy.
 */
export const InvoiceReminderHistory = ({ invoiceId }: InvoiceReminderHistoryProps) => {
  const [reminders, setReminders] = useState<BillingReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    getInvoiceReminders(invoiceId, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setReminders(loaded);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          err instanceof ApiError && err.message
            ? err.message
            : 'Could not load the reminder history.'
        );
        setLoading(false);
      });

    return () => controller.abort();
  }, [invoiceId]);

  if (loading) {
    return <p className="billing-panel__note">Loading reminders...</p>;
  }

  if (error) {
    return (
      <p className="admin-error" role="alert">
        {error}
      </p>
    );
  }

  if (reminders.length === 0) {
    return (
      <p className="billing-panel__note" data-testid={`billing-reminders-empty-${invoiceId}`}>
        No reminders have been attempted for this invoice.
      </p>
    );
  }

  return (
    <ul className="billing-panel__reminders" data-testid={`billing-reminders-${invoiceId}`}>
      {reminders.map((reminder) => {
        const reason = reasonFor(reminder);
        return (
          <li
            key={reminder.id}
            className={`billing-panel__reminder billing-panel__reminder--${reminder.outcome}`}
            data-testid={`billing-reminder-${reminder.id}`}
          >
            <span className="billing-panel__reminder-head">
              <span className="billing-panel__reminder-outcome">
                {REMINDER_OUTCOME_LABEL[reminder.outcome]}
              </span>
              <span className="billing-panel__reminder-detail">
                {REMINDER_KIND_LABEL[reminder.kind]} reminder for {dateOnlyCell(reminder.anchor)}
              </span>
              <span
                className="billing-panel__reminder-detail"
                data-testid={`billing-reminder-source-${reminder.id}`}
              >
                {REMINDER_SOURCE_LABEL[reminder.source]}
              </span>
            </span>
            <span className="billing-panel__reminder-detail">
              {reminder.sentAt
                ? `Sent ${instantCell(reminder.sentAt)}`
                : reminder.claimedAt
                  ? `Picked up ${instantCell(reminder.claimedAt)}, never sent`
                  : 'Not picked up yet'}
              {reminder.templateKey ? ` - template ${reminder.templateKey}` : ''}
            </span>
            <span className="billing-panel__reminder-detail">
              To: {reminder.toAddresses.length > 0 ? reminder.toAddresses.join(', ') : 'nobody'}
              {reminder.ccAddresses.length > 0 ? ` - Cc: ${reminder.ccAddresses.join(', ')}` : ''}
            </span>
            {reason && <span className="billing-panel__reminder-reason">{reason}</span>}
          </li>
        );
      })}
    </ul>
  );
};
